/**
 * Image Optimizer
 * Resizes and compresses images for web using Canvas API
 */

// Target dimensions by image type
const IMAGE_SIZES = {
  hero: { maxWidth: 1920, maxHeight: 1080 },
  article: { maxWidth: 1200, maxHeight: 800 },
  thumbnail: { maxWidth: 600, maxHeight: 400 },
  author: { maxWidth: 400, maxHeight: 400 },
  default: { maxWidth: 1200, maxHeight: 900 },
}

// Quality settings
const WEBP_QUALITY = 0.85
const JPEG_QUALITY = 0.85

/**
 * Detect image type from filename
 * @param {string} filename
 * @returns {string} Image type (hero, article, thumbnail, author, default)
 */
function detectImageType(filename) {
  const lower = filename.toLowerCase()
  if (lower.includes('hero') || lower.includes('accueil') || lower.includes('banner')) {
    return 'hero'
  }
  if (lower.includes('thumb') || lower.includes('vignette')) {
    return 'thumbnail'
  }
  if (lower.includes('auteur') || lower.includes('author') || lower.includes('avatar')) {
    return 'author'
  }
  if (lower.includes('guide') || lower.includes('article')) {
    return 'article'
  }
  return 'default'
}

/**
 * Load an image from URL into an HTMLImageElement
 * @param {string} url - Image URL
 * @returns {Promise<HTMLImageElement>}
 */
function loadImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous' // Enable CORS for external images
    img.onload = () => resolve(img)
    img.onerror = (e) => reject(new Error(`Failed to load image: ${url}`))
    img.src = url
  })
}

/**
 * Calculate new dimensions while maintaining aspect ratio
 * @param {number} width - Original width
 * @param {number} height - Original height
 * @param {number} maxWidth - Maximum width
 * @param {number} maxHeight - Maximum height
 * @returns {{ width: number, height: number }}
 */
function calculateDimensions(width, height, maxWidth, maxHeight) {
  let newWidth = width
  let newHeight = height

  // Scale down if larger than max dimensions
  if (width > maxWidth) {
    newWidth = maxWidth
    newHeight = Math.round(height * (maxWidth / width))
  }
  if (newHeight > maxHeight) {
    newHeight = maxHeight
    newWidth = Math.round(newWidth * (maxHeight / newHeight))
  }

  return { width: newWidth, height: newHeight }
}

/**
 * Optimize an image: resize and compress to WebP (or JPEG fallback)
 * @param {string} url - Image URL
 * @param {string} filename - Original filename (used to detect type)
 * @param {Object} options - Optional overrides
 * @returns {Promise<{ blob: Blob, format: string, originalSize: number, optimizedSize: number }>}
 */
export async function optimizeImage(url, filename, options = {}) {
  const {
    quality = WEBP_QUALITY,
    preferWebP = true,
  } = options

  // Detect image type and get target dimensions
  const imageType = detectImageType(filename)
  const targetSize = IMAGE_SIZES[imageType]

  console.log(`🖼️ [OPTIMIZE] Processing ${filename} (type: ${imageType}, max: ${targetSize.maxWidth}x${targetSize.maxHeight})`)

  // Load the image
  const img = await loadImage(url)
  const originalWidth = img.naturalWidth
  const originalHeight = img.naturalHeight

  // Calculate new dimensions
  const { width, height } = calculateDimensions(
    originalWidth,
    originalHeight,
    targetSize.maxWidth,
    targetSize.maxHeight
  )

  console.log(`🖼️ [OPTIMIZE] Resizing ${originalWidth}x${originalHeight} → ${width}x${height}`)

  // Create canvas and draw resized image
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')

  // Use high-quality image smoothing
  ctx.imageSmoothingEnabled = true
  ctx.imageSmoothingQuality = 'high'

  // Draw the image
  ctx.drawImage(img, 0, 0, width, height)

  // Try WebP first, fallback to JPEG
  let blob
  let format = 'webp'

  if (preferWebP) {
    blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/webp', quality))
  }

  // Fallback to JPEG if WebP not supported or failed
  if (!blob || blob.size === 0) {
    format = 'jpeg'
    blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', JPEG_QUALITY))
  }

  // Estimate original size (fetch the original to compare)
  let originalSize = 0
  try {
    const response = await fetch(url)
    const originalBlob = await response.blob()
    originalSize = originalBlob.size
  } catch {
    originalSize = blob.size * 2 // Rough estimate
  }

  const savings = originalSize > 0 ? Math.round((1 - blob.size / originalSize) * 100) : 0

  console.log(`🖼️ [OPTIMIZE] ${filename}: ${Math.round(originalSize/1024)}KB → ${Math.round(blob.size/1024)}KB (${format}, -${savings}%)`)

  return {
    blob,
    format,
    width,
    height,
    originalSize,
    optimizedSize: blob.size,
    savings,
  }
}

/**
 * Get optimized filename (change extension to .webp or .jpg)
 * @param {string} filename - Original filename
 * @param {string} format - Target format (webp or jpeg)
 * @returns {string}
 */
export function getOptimizedFilename(filename, format) {
  const baseName = filename.replace(/\.(png|jpg|jpeg|gif|webp)$/i, '')
  return `${baseName}.${format === 'jpeg' ? 'jpg' : format}`
}

/**
 * Batch optimize multiple images
 * @param {Array<{ url: string, filename: string }>} images
 * @param {Function} onProgress - Progress callback
 * @returns {Promise<Array<{ filename: string, blob: Blob, format: string }>>}
 */
export async function optimizeImages(images, onProgress) {
  const results = []
  let totalSavings = 0

  for (let i = 0; i < images.length; i++) {
    const img = images[i]
    onProgress?.({ current: i + 1, total: images.length, filename: img.filename })

    try {
      const result = await optimizeImage(img.url, img.filename)
      const newFilename = getOptimizedFilename(img.filename, result.format)

      results.push({
        originalFilename: img.filename,
        filename: newFilename,
        blob: result.blob,
        format: result.format,
        savings: result.savings,
      })

      totalSavings += result.originalSize - result.optimizedSize
    } catch (error) {
      console.warn(`🖼️ [OPTIMIZE] Failed to optimize ${img.filename}:`, error.message)
      // Try to use original image as fallback
      try {
        const response = await fetch(img.url)
        const blob = await response.blob()
        results.push({
          originalFilename: img.filename,
          filename: img.filename,
          blob,
          format: 'original',
          savings: 0,
        })
      } catch {
        console.error(`🖼️ [OPTIMIZE] Could not fetch original ${img.filename}`)
      }
    }
  }

  console.log(`🖼️ [OPTIMIZE] Total savings: ${Math.round(totalSavings / 1024)}KB`)

  return results
}

export default {
  optimizeImage,
  optimizeImages,
  getOptimizedFilename,
}
